import React, { useEffect, useState } from 'react';
import { observationService } from '../../services/observationService';
import { DataTypesEnum, ObservableLevelEnumValue } from '../../data/enums';
import { Checkbox } from './BaseCheckbox';
import { Label } from './Label';
import { Tooltip } from './Tooltip';
import { cacheInvalidationService } from '../../services/cacheInvalidationService';

interface CheckboxWithLabelProps {
  title?: string;
  label: string;
  infoTooltipKey?: string;
  onChange?: (checked: boolean) => void;
  onDefaultLoad?: (checked: boolean) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  titleStyle?: string;
  inspectionCategoryTypeId: string;
  checkedColor?: string;
}

const CheckboxWithLabel: React.FC<CheckboxWithLabelProps> = ({
  title,
  label,
  infoTooltipKey,
  path,
  dataLevel,
  entityId,
  entityExternalId,
  titleStyle,
  inspectionId,
  inspectionCategoryTypeId,
  onChange,
  checkedColor = 'checked:bg-brandOrange',
  onDefaultLoad,
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const _isChecked = event.target.checked;
    setIsChecked(_isChecked);
    const response = await observationService.upsertObservation({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId: entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path,
      value: _isChecked.toString(),
    });
    console.log(`upsertedFor.${path}.${_isChecked}`);
    if (response !== '') {
      // Clear all children observations if the checkbox is unchecked
      if (!_isChecked) {
        await observationService.deleteObservationsByProps({
          dataLevel: dataLevel,
          entityId: entityId,
          entityExternalId: entityExternalId,
          inspectionCategoryId: inspectionCategoryTypeId,
          inspectionId: inspectionId,
          path,
        });
      }

      await cacheInvalidationService.invalidateInspection(inspectionId);

      onChange?.(_isChecked);
    }
  };

  // Load Default
  useEffect(() => {
    const getDefaultValue = async () => {
      const observation = await observationService.getObservation({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId: entityExternalId,
        inspectionId: inspectionId,
        inspectionCategoryId: inspectionCategoryTypeId,
        path: path,
      });

      if (observation) {
        const value = observationService.convertValue<boolean>(
          observation.value,
          DataTypesEnum.boolean,
        );
        setIsChecked(value);
        onDefaultLoad?.(value);
      }
    };

    void getDefaultValue();
  }, [inspectionCategoryTypeId, inspectionId, dataLevel, entityId, path]);

  return (
    <div className="w-full space-y-2 py-4">
      {title && (
        <div className="flex items-start justify-between">
          <div className="w-full pr-8 lg:w-1/2">
            <Label className={`pb-2 ${titleStyle}`} titleType="fieldTitle">
              {title}
            </Label>
          </div>
          {title && infoTooltipKey && <Tooltip tooltipKey={infoTooltipKey} />}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex w-full items-center space-x-6 hover:bg-brandLight/30">
          <Checkbox
            id={'checkbox-input-' + path.toLowerCase().split('.').join('-')}
            key={path.toLowerCase().split('.').join('-')}
            checked={isChecked}
            onChange={handleChange}
            className={`${checkedColor}`}
          />
          <label
            htmlFor={
              'checkbox-input-' + path.toLowerCase().split('.').join('-')
            }
            className="flex-1 cursor-pointer text-ink hover:bg-brandLight/30"
          >
            {label}
          </label>
        </div>
        {!title && infoTooltipKey && <Tooltip tooltipKey={infoTooltipKey} />}
      </div>
    </div>
  );
};

export default CheckboxWithLabel;
